import { useState } from 'react';
import { Loader2, AlertTriangle, CheckCircle2, XCircle, Search } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { reservesWaterfallAPI } from '../../lib/api';

function FieldRow({ label, value, present }) {
  return (
    <div className="flex items-center justify-between py-1">
      <div className="flex items-center gap-2 text-sm">
        {present ? (
          <CheckCircle2 className="h-3.5 w-3.5 text-green-600 shrink-0" />
        ) : (
          <XCircle className="h-3.5 w-3.5 text-red-500 shrink-0" />
        )}
        <span className="font-mono text-xs">{label}</span>
      </div>
      {value != null && (
        <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{String(value)}</code>
      )}
    </div>
  );
}

export function WaterfallDeployCheckCard() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const runCheck = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const { data } = await reservesWaterfallAPI.get('br_ltd');
      if (data.error) {
        setError(data.error);
        return;
      }
      const vat = data.waterfall?.vat_reserve_target || {};
      const corp = data.waterfall?.corp_tax_reserve_target || {};
      setResult({
        scopePresent: vat.scope != null,
        scopeValue: vat.scope ?? null,
        fyEndPresent: corp.financial_year_end != null,
        fyEndValue: corp.financial_year_end ?? null,
        periodStartPresent: corp.period_start != null,
        periodStartValue: corp.period_start ?? null,
        periodEndPresent: corp.period_end != null,
        periodEndValue: corp.period_end ?? null,
      });
    } catch (err) {
      const msg =
        err.response?.data?.detail ||
        err.response?.data?.error ||
        err.message ||
        'Request failed';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const isNewBuild =
    result?.scopePresent && result?.periodStartPresent && result?.periodEndPresent;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Reserves Waterfall Deploy Check</CardTitle>
          <Button variant="outline" size="sm" onClick={runCheck} disabled={loading}>
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Search className="h-4 w-4 mr-2" />
            )}
            Check
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {!result && !error && !loading && (
          <p className="text-sm text-muted-foreground">
            Press Check to verify the waterfall patch deployed.
          </p>
        )}

        {error && (
          <div className="flex items-start gap-2 text-sm text-red-500">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {result && (
          <div className="space-y-3">
            {/* Verdict */}
            <div className="flex items-center gap-2">
              {isNewBuild ? (
                <>
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                    new build live
                  </Badge>
                </>
              ) : (
                <>
                  <XCircle className="h-4 w-4 text-red-500" />
                  <Badge variant="destructive">stale build</Badge>
                </>
              )}
            </div>

            {/* Field presence */}
            <div className="border rounded-md px-3 py-1 divide-y">
              <FieldRow
                label="vat_reserve_target.scope"
                value={result.scopeValue}
                present={result.scopePresent}
              />
              <FieldRow
                label="financial_year_end"
                value={result.fyEndValue}
                present={result.fyEndPresent}
              />
              <FieldRow
                label="corp_tax_reserve_target.period_start"
                value={result.periodStartValue}
                present={result.periodStartPresent}
              />
              <FieldRow
                label="corp_tax_reserve_target.period_end"
                value={result.periodEndValue}
                present={result.periodEndPresent}
              />
            </div>

            {/* FY eyeball check */}
            {result.fyEndPresent && (
              <p className="text-xs text-muted-foreground">
                FY end {result.fyEndValue} produces period {result.periodStartValue} to {result.periodEndValue}.
                Verify this matches br_ltd's Companies House accounting reference date.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
